import { useState } from "react";

import { Button, Icon_Close24, TextField } from "@shapr3d/frontend-lib";
import { FormattedMessage } from "react-intl";

import { useDebounce } from "../../hooks/useDebounce";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { isDefined } from "../../utils/typeUtils";
import { AddToSpaceModal } from "./AddToSpaceModal/AddToSpaceModal";
import { RemoveFromSpaceModal } from "./RemoveFromSpaceModal/RemoveFromSpaceModal";

import style from "./MemberSelectionFilterRow.module.css";

const MemberSelectionFilterRow = ({
  filter,
  setFilter,
  selection,
  setSelection,
  isRemoveFromSpaceButtonDisabled,
}: {
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  selection: Array<string | null | undefined>;
  setSelection: React.Dispatch<React.SetStateAction<Array<string | null | undefined>>>;
  isRemoveFromSpaceButtonDisabled: boolean;
}) => {
  const { m } = useTranslation();
  const tracker = useTracker();

  const [isAddToSpaceModalOpen, setIsAddToSpaceModalOpen] = useState(false);
  const [isRemoveFromSpaceModalOpen, setIsRemoveFromSpaceModalOpen] = useState(false);

  const debouncedTrackFilterMembers = useDebounce(() => {
    tracker.trackButtonClick({
      action: ElementClickAction.CHANGE_MEMBERS_FILTER,
    });
  }, 1000);

  return (
    <>
      {!selection.length && (
        <TextField
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            debouncedTrackFilterMembers();
          }}
          onFocus={() =>
            tracker.trackButtonClick({
              action: ElementClickAction.FOCUS_MEMBERS_FILTER,
            })
          }
          className={style.filterTextField}
          placeholder="Filter members by name, email or space"
          clearable={Boolean(filter)}
          onClear={() => {
            setFilter("");
            tracker.trackButtonClick({
              action: ElementClickAction.CLEAR_MEMBERS_FILTER,
            });
          }}
        />
      )}
      {Boolean(selection.length) && (
        <div className={style.removalPanel}>
          <div className={style.titleContainer}>
            <Button
              variant="borderless"
              onClick={() => {
                setSelection([]);
                tracker.trackButtonClick({
                  action: ElementClickAction.DESELECT_MEMBERS,
                  details: "all",
                  location: ElementClickLocation.MEMBERS_TABLE_FILTER_ROW,
                });
              }}
              className={style.closeButton}
            >
              <Icon_Close24 />
            </Button>
            <div className={style.title}>
              <FormattedMessage
                id={m.MembersPage.SelectionMessage.id}
                defaultMessage={m.MembersPage.SelectionMessage.defaultMessage}
                values={{
                  count: selection.length,
                }}
              />
            </div>
          </div>
          <div className={style.buttonContainer}>
            <Button
              variant="primary"
              onClick={() => {
                setIsAddToSpaceModalOpen(true);
                tracker.trackButtonClick({
                  action: ElementClickAction.ADD_TO_SPACE,
                  location: ElementClickLocation.MEMBERS_TABLE_FILTER_ROW,
                });
              }}
            >
              Add to space...
            </Button>
            <Button
              variant="grayscale"
              onClick={() => {
                setIsRemoveFromSpaceModalOpen(true);
                tracker.trackButtonClick({
                  action: ElementClickAction.REMOVE_FROM_SPACE,
                  location: ElementClickLocation.MEMBERS_TABLE_FILTER_ROW,
                });
              }}
              className={style.removalButton}
              disabled={isRemoveFromSpaceButtonDisabled}
            >
              Remove from space...
            </Button>
          </div>
        </div>
      )}
      <AddToSpaceModal
        open={isAddToSpaceModalOpen}
        onClose={() => setIsAddToSpaceModalOpen(false)}
        userIDs={selection.filter(isDefined)}
      />
      <RemoveFromSpaceModal
        open={isRemoveFromSpaceModalOpen}
        onClose={() => setIsRemoveFromSpaceModalOpen(false)}
        userIDs={selection.filter(isDefined)}
      />
    </>
  );
};

export default MemberSelectionFilterRow;
